// Mastery Breakdown tab, built to the PM's mockup.
//
//   Cluster  ->  open  ->  Subtopic (with its curriculum year)
//
// One table with indented rows rather than two panels or a modal, so the tutor
// keeps every score they have already read on screen while opening the level
// below, and the bar column stays on one axis all the way through.
//
// THE TOPIC LEVEL IS GONE. It sat between these two and, in this curriculum,
// nearly always repeated the words of the cluster above it - a topic called
// "Number and Place Value" inside a cluster of the same name - so opening a
// cluster showed the tutor its own heading again before anything useful. The
// server still resolves the topic internally to map questions to clusters.
//
// Each subtopic carries the year its material belongs to, which is the whole
// point once a prerequisite test has run: a Year 6 student's cluster opens to
// show Year 2 and Year 3 subtopics underneath the Year 6 ones.
//
// This replaces "Mastery by Skill Area", which showed one row per cluster in a
// grid of band COLUMNS. The band is now a bar plus a label on the row itself,
// which is what makes three levels fit: a column grid cannot be indented
// without the columns drifting out of line.
//
// Both scopes arrive on every node from the server, so the dropdown swaps
// `base` for `overall` with no refetch and no chance of mixing a base
// percentage into an overall row.

import React from "react"
import type {
  BmdMasteryCluster,
  BmdMasteryStats,
} from "../types/bmd-report.types"

// ---------------------------------------------------------------------------
// Bands
// ---------------------------------------------------------------------------

type Band = {
  key: string
  label: string
  range: string
  /** Bar fill. Darker is stronger, so the column reads as a gradient. */
  color: string
  min: number
  max: number
}

const BANDS: Band[] = [
  { key: "very_strong", label: "Very Strong", range: "80–100%", color: "#12386e", min: 80, max: 100 },
  { key: "strong",      label: "Strong",      range: "60–79%",  color: "#1b5fae", min: 60, max: 79 },
  { key: "developing",  label: "Developing",  range: "40–59%",  color: "#2b7de0", min: 40, max: 59 },
  { key: "weak",        label: "Weak",        range: "20–39%",  color: "#63a2ea", min: 20, max: 39 },
  { key: "very_weak",   label: "Very Weak",   range: "0–19%",   color: "#a8cbf2", min: 0,  max: 19 },
]

const NOT_ATTEMPTED: Band = {
  key: "not_attempted",
  label: "Not attempted",
  range: "",
  color: "#e8edf5",
  min: -1,
  max: -1,
}

const VERY_WEAK_KEY = "very_weak"

function bandFor(stats: BmdMasteryStats): Band {
  // Never sat is not a score. Filing it under Very Weak made untested material
  // read as failed material, which is the reading this column exists to avoid.
  if (!stats.attempted) return NOT_ATTEMPTED
  for (const b of BANDS) {
    if (stats.score_pct >= b.min && stats.score_pct <= b.max) return b
  }
  return BANDS[BANDS.length - 1]
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

const INK = "#16264a"
const MUTED = "#7d8aa3"
const BODY = "#5a6b8c"
const TRACK = "#e8edf5"
const HEAD_BG = "#f5f7fa"

/** Column headings. Applied per-cell; see the note in <thead>. */
const HEAD_CELL: React.CSSProperties = {
  backgroundColor: HEAD_BG,
  color: MUTED,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.04em",
}
const CLUSTER_BG = "#eef2f8"
const RULE = "#eceff5"
const PILL_BG = "#e3ecf8"
const PILL_TEXT = "#1f4d8f"
const PILL_IDLE_BG = "#eef1f6"

type MasteryMode = "base" | "overall"

/**
 * Which scope the tab opens on.
 *
 * The PM asked for the report to default to Overall so the prerequisite test is
 * counted unless the tutor deliberately narrows to the base test. Kept as a
 * constant because it is a product decision that has already been reversed once
 * and is a one-word change here.
 */
const DEFAULT_MASTERY_MODE: MasteryMode = "overall"

function statsFor(
  node: { base: BmdMasteryStats; overall: BmdMasteryStats },
  mode: MasteryMode,
): BmdMasteryStats {
  return mode === "overall" ? node.overall : node.base
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

// One flattened row, whatever level produced it. Flattening keeps the table a
// single <tbody>, so the columns stay aligned all the way down instead of each
// level laying out its own grid.
type Row = {
  key: string
  level: 0 | 1
  label: string
  /** Only on subtopics: the curriculum year, drawn as a chip after the name. */
  yearName?: string
  stats: BmdMasteryStats
  /** Null on a leaf, or on a cluster with nothing to reveal in this scope. */
  expandKey: string | null
  isOpen: boolean
}

const MasteryBar: React.FC<{ stats: BmdMasteryStats; band: Band }> = ({
  stats,
  band,
}) => {
  if (!stats.attempted) {
    return (
      <div
        className="d-flex align-items-center px-2"
        style={{
          height: 16,
          borderRadius: 8,
          backgroundColor: TRACK,
          color: MUTED,
          fontSize: 11,
        }}
      >
        No result yet
      </div>
    )
  }
  return (
    <div
      style={{ height: 16, borderRadius: 8, backgroundColor: TRACK, overflow: "hidden" }}
      role="img"
      aria-label={`${stats.score_pct}%`}
    >
      <div
        style={{
          // A 0% score still gets a sliver, otherwise the row looks like it
          // failed to load rather than like the student got none of it right.
          width: `${Math.max(stats.score_pct, 1)}%`,
          height: "100%",
          borderRadius: 8,
          backgroundColor: band.color,
        }}
      ></div>
    </div>
  )
}

const BandPill: React.FC<{ band: Band }> = ({ band }) => (
  <span
    style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      backgroundColor: band.key === NOT_ATTEMPTED.key ? PILL_IDLE_BG : PILL_BG,
      color: band.key === NOT_ATTEMPTED.key ? MUTED : PILL_TEXT,
      whiteSpace: "nowrap",
    }}
  >
    {band.label}
  </span>
)

export type BmdMasteryBreakdownTabProps = {
  masteryTree: BmdMasteryCluster[]
  /** e.g. "Year 6 (2024)". Shown in the subtitle, not as a column. */
  baseYearLabel: string
}

export const BmdMasteryBreakdownTab: React.FC<
  BmdMasteryBreakdownTabProps
> = ({ masteryTree, baseYearLabel }) => {
  const [mode, setMode] = React.useState<MasteryMode>(DEFAULT_MASTERY_MODE)
  const [openKeys, setOpenKeys] = React.useState<Set<string>>(new Set())

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Base test view: leave out anything the prerequisite chain reached at a
  // lower year. A subtopic with no base answers has nothing to say in this
  // scope, and listing it would show "Not attempted" against material the base
  // test was never going to cover.
  const subTopicsOf = React.useCallback(
    (cluster: BmdMasteryCluster) => {
      const subs = cluster.sub_topics || []
      return mode === "base"
        ? subs.filter((sub) => sub.base.answered > 0)
        : subs
    },
    [mode],
  )

  const rows: Row[] = React.useMemo(() => {
    const out: Row[] = []
    for (const cluster of masteryTree) {
      // A cluster that only exists because the chain dropped into a lower year
      // is not part of the base test at all.
      if (mode === "base" && cluster.in_base_year === false) continue

      const clusterKey = `c-${cluster.strand_id}`
      const clusterOpen = openKeys.has(clusterKey)
      const subs = subTopicsOf(cluster)

      out.push({
        key: clusterKey,
        level: 0,
        label: cluster.strand_name,
        stats: statsFor(cluster, mode),
        // Filtered before the row is built, so a cluster with nothing to show
        // in this scope is not given a chevron that opens onto nothing.
        expandKey: subs.length > 0 ? clusterKey : null,
        isOpen: clusterOpen,
      })
      if (!clusterOpen) continue

      for (const sub of subs) {
        out.push({
          key: `${clusterKey}-s-${sub.sub_topic_id}`,
          level: 1,
          label: sub.sub_topic_name || "—",
          yearName: sub.year_name || undefined,
          stats: statsFor(sub, mode),
          expandKey: null,
          isOpen: false,
        })
      }
    }
    return out
  }, [masteryTree, openKeys, mode, subTopicsOf])

  // Headline figures. Summed from the cluster rows rather than counted
  // separately, so the number at the top can never disagree with the rows
  // underneath it.
  const summary = React.useMemo(() => {
    let correct = 0
    let answered = 0
    let veryWeak = 0
    for (const c of masteryTree) {
      const s = statsFor(c, mode)
      correct += s.correct
      answered += s.answered
      if (bandFor(s).key === VERY_WEAK_KEY) veryWeak += 1
    }
    return {
      correct,
      answered,
      pct: answered > 0 ? Math.round((correct / answered) * 100) : 0,
      veryWeak,
      clusters: masteryTree.length,
    }
  }, [masteryTree, mode])

  const allClusterKeys = React.useMemo(
    () => masteryTree.map((c) => `c-${c.strand_id}`),
    [masteryTree],
  )
  const anyOpen = allClusterKeys.some((k) => openKeys.has(k))

  const header = (
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
      <div style={{ minWidth: 0 }}>
        <div className="fw-bold" style={{ color: INK, fontSize: 17 }}>
          Mastery Breakdown
        </div>
        <div style={{ color: BODY, fontSize: 12.5 }} className="mt-1">
          {baseYearLabel} · how much of each cluster this student got right.
          Open a cluster to see its subtopics.
        </div>
      </div>
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        {/* Not in the mockup, but a cluster can open onto forty-odd subtopics
            after a deep prerequisite test, and closing them one at a time is a
            chore. Styled explicitly: the theme's outline-secondary is so faint
            here that the button read as disabled. */}
        <button
          type="button"
          className="btn btn-sm"
          style={{
            fontSize: 12,
            color: PILL_TEXT,
            borderColor: "#cbd7e8",
            backgroundColor: "#fff",
          }}
          onClick={() =>
            setOpenKeys(anyOpen ? new Set() : new Set(allClusterKeys))
          }
          disabled={masteryTree.length === 0}
        >
          {anyOpen ? "Collapse all" : "Expand all"}
        </button>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={mode}
          onChange={(e) => setMode(e.target.value as MasteryMode)}
          aria-label="Mastery scope"
        >
          <option value="base">Base test</option>
          <option value="overall">Overall (with prerequisite test)</option>
        </select>
      </div>
    </div>
  )

  if (masteryTree.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm p-4 mb-4">
        {header}
        <p style={{ color: BODY }} className="mb-0 font-size-13">
          No data yet.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4">
      {header}

      {/* Headline: the one number a tutor reads first, then the count of
          clusters in the worst band, which is what they act on. */}
      <div className="d-flex align-items-baseline flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-baseline gap-2">
          <span className="fw-bold" style={{ color: INK, fontSize: 26 }}>
            {summary.pct}%
          </span>
          <span style={{ color: BODY, fontSize: 12.5 }}>
            correct overall · {summary.correct} of {summary.answered}
          </span>
        </div>
        {summary.veryWeak > 0 ? (
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#d94a5a",
                display: "inline-block",
              }}
              aria-hidden="true"
            ></span>
            <span style={{ color: BODY, fontSize: 12.5 }}>
              {summary.veryWeak} of {summary.clusters} cluster
              {summary.clusters === 1 ? "" : "s"} are Very Weak
            </span>
          </div>
        ) : null}
      </div>

      <div className="table-responsive">
        <table
          className="table table-borderless align-middle mb-0"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead>
            {/* Every colour and size goes on the CELLS, never on the row.
                Bootstrap's `.table > :not(caption) > * > *` sets background on
                each cell and paints an inset shadow over it, so a background on
                the <tr> is covered up; the app's stylesheet sizes `th` too, so a
                font-size on the row loses to it. Both looked fine in isolation
                and neither reached the screen. */}
            <tr>
              <th className="text-uppercase" style={{ ...HEAD_CELL, width: "38%" }}>
                Cluster and subtopic
              </th>
              <th className="text-uppercase" style={{ ...HEAD_CELL, width: "11%" }}>
                Correct
              </th>
              <th className="text-uppercase" style={{ ...HEAD_CELL, width: "30%" }}>
                Mastery
              </th>
              <th
                className="text-uppercase text-end"
                style={{ ...HEAD_CELL, width: "8%" }}
              >
                %
              </th>
              <th
                className="text-uppercase text-end"
                style={{ ...HEAD_CELL, width: "13%" }}
              >
                Band
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const band = bandFor(r.stats)
              const expandable = r.expandKey !== null
              const missing = !r.stats.attempted
              // Per-cell, for the same reason as the header: a background set
              // on the <tr> is painted over by Bootstrap's table cell styles,
              // so the cluster band never appeared.
              const cell: React.CSSProperties = {
                backgroundColor: r.level === 0 ? CLUSTER_BG : undefined,
                borderBottom: `1px solid ${RULE}`,
              }
              return (
                <tr
                  key={r.key}
                  style={{ cursor: expandable ? "pointer" : "default" }}
                  onClick={() => {
                    if (r.expandKey) toggle(r.expandKey)
                  }}
                >
                  <td
                    style={{
                      ...cell,
                      paddingLeft: 10 + r.level * 22,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {expandable ? (
                      <i
                        className={`fas fa-chevron-${r.isOpen ? "down" : "right"} me-2`}
                        style={{ fontSize: 9, color: MUTED }}
                        aria-hidden="true"
                      ></i>
                    ) : (
                      // Keeps a leaf label on the same left edge as a sibling
                      // that does have a chevron.
                      <span
                        className="d-inline-block me-2"
                        style={{ width: 9 }}
                      ></span>
                    )}
                    <span
                      style={{
                        color: missing ? MUTED : INK,
                        fontSize: r.level === 1 ? 13.5 : 14,
                        fontWeight: r.level === 1 ? 400 : 600,
                      }}
                    >
                      {r.label}
                    </span>
                    {/* The curriculum year, for reference. It is the subtopic's
                        own year, not the year of the test, so a cluster opened
                        after a prerequisite test reads as a ladder down through
                        the years. */}
                    {r.yearName ? (
                      <span
                        className="ms-2"
                        style={{
                          display: "inline-block",
                          padding: "1px 7px",
                          borderRadius: 4,
                          backgroundColor: HEAD_BG,
                          color: MUTED,
                          fontSize: 11.5,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.yearName}
                      </span>
                    ) : null}
                  </td>

                  <td style={{ ...cell, fontSize: 12.5, color: missing ? MUTED : BODY }}>
                    {missing
                      ? "not sat"
                      : `${r.stats.correct} of ${r.stats.answered}`}
                  </td>

                  <td style={cell}>
                    <MasteryBar stats={r.stats} band={band} />
                  </td>

                  <td
                    className="text-end fw-bold"
                    style={{ ...cell, fontSize: 12.5, color: missing ? MUTED : INK }}
                  >
                    {missing ? "—" : `${r.stats.score_pct}%`}
                  </td>

                  <td className="text-end" style={cell}>
                    {/* Clusters get a pill; subtopics get plain text, so the
                        eye can tell the two levels apart at a glance even when
                        several clusters are open. */}
                    {r.level === 1 ? (
                      <span
                        style={{
                          fontSize: 12.5,
                          color: missing ? MUTED : BODY,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {band.label}
                      </span>
                    ) : (
                      <BandPill band={band} />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
        <span
          className="text-uppercase fw-semibold"
          style={{ color: MUTED, fontSize: 10.5, letterSpacing: "0.04em" }}
        >
          Bands
        </span>
        {[...BANDS, NOT_ATTEMPTED].map((b) => (
          <span
            key={b.key}
            className="d-inline-flex align-items-center"
            style={{ color: BODY, fontSize: 11.5 }}
          >
            <span
              className="me-2 d-inline-block"
              style={{
                width: 22,
                height: 8,
                borderRadius: 4,
                backgroundColor: b.color,
              }}
              aria-hidden="true"
            ></span>
            {b.label}
            {b.range ? ` ${b.range}` : ""}
          </span>
        ))}
      </div>
    </div>
  )
}

export default BmdMasteryBreakdownTab
